/**
 * The placeholder RevenueCat Web Billing key (credential C1) used until the
 * real `rcb_` key exists. The ONE place "dummy" is defined: everything that
 * asks "is this key real?" goes through `usableRevenueCatKey`, and the
 * production build gate goes through `assertNoDummyKeyInProduction`.
 *
 * A dummy must never read as working billing — it is shaped like a key so the
 * rest of the config is exercised, and refused everywhere it could matter.
 */
export const DUMMY_REVENUECAT_KEY = 'rcb_dummy_replace_with_real_key'

/**
 * The key the SDK may be configured with, or null. Null for a missing key, the
 * dummy, or anything not shaped like a Web Billing public key (`rcb_…`): the
 * caller then has no billing and every gate stays locked.
 */
export function usableRevenueCatKey(key: string | undefined): string | null {
  if (!key || key === DUMMY_REVENUECAT_KEY || !key.startsWith('rcb_')) return null
  return key
}

/**
 * Build-time refusal, called from `vite.config.ts`. A production bundle with
 * the dummy would ship billing that silently never works, so it fails the
 * build instead. Production-only: dev and staging run the dummy by design.
 *
 * A MISSING key in production is not refused here — see the C1 ceremony in
 * `config/required.ts`: moving the name to REQUIRED is what makes absence fail.
 */
export function assertNoDummyKeyInProduction(mode: string, key: string | undefined): void {
  if (mode === 'production' && key === DUMMY_REVENUECAT_KEY) {
    throw new Error(
      'VITE_REVENUECAT_WEB_KEY is the dummy placeholder in a production build. ' +
        'Set the real rcb_ key (credential C1) — a dummy ships billing that never works.',
    )
  }
}
